import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import { 
  ShoppingCart, 
  Plus, 
  Minus, 
  Trash2, 
  Heart,
  Star,
  Gift,
  Percent,
  CreditCard,
  Lock,
  Truck,
  Clock,
  MapPin,
  Code,
  Eye,
  Activity,
  Sun,
  Moon
} from 'lucide-react'
import { useApiLogs } from '../contexts/ApiLogsContext'
import DemoTutorial from '../components/DemoTutorial'

interface FlowerItem {
  id: string
  name: string
  price: number
  image: string
  quantity: number
  description: string
  rating: number
}

interface UserSelectedPerk {
  id: string
  title: string
  description: string
  value: string
  image: string | null
  category: 'shipping' | 'discount' | 'gift' | 'points'
  icon: React.ComponentType<any>
  selected: boolean
  sessionId?: string
  campaignId?: string
  offerId?: string
}

interface ApiRequest {
  method: string
  url: string
  headers: Record<string, string>
  body: any
}

interface ApiResponse {
  status: number
  statusText: string
  data: any
  timestamp: string
}

interface ApiLog {
  request: ApiRequest
  response: ApiResponse | null
  loading: boolean
  error: string | null
}

// Define interfaces for selection history items
interface SelectionHistoryItem {
  id: string;
  timestamp: string;
  action: string;
  status: 'success' | 'error';
  curlCommand?: string;
  response?: any;
  error?: string;
  totalPerks?: number;
  perkTitle?: string;
  perkId?: string;
  campaignId?: string;
}

const USPFlowerStorePage: React.FC = () => {
  const navigate = useNavigate()
  const { addApiLog, updateApiLog, addSelectionHistory, getLogsByPage, getSelectionHistoryByPage, clearSelectionHistory } = useApiLogs()
  
  // Stub function to avoid errors from duplicate setSelectionHistory calls - these should be converted to addSelectionHistory
  const setSelectionHistory = (_fn: (prev: SelectionHistoryItem[]) => SelectionHistoryItem[]) => {
    // This is a stub - we're now using addSelectionHistory instead
  };
  const [apiKey, setApiKey] = useState<string>('')
  const [pubUserId, setPubUserId] = useState<string>('')
  const [sessionId, setSessionId] = useState<string>('')
  const [cartItems, setCartItems] = useState<FlowerItem[]>([
    {
      id: '1',
      name: 'Red Rose Bouquet',
      price: 45.99,
      image: 'https://images.unsplash.com/photo-1518895949257-7621c3c786d7?w=400&h=400&fit=crop',
      quantity: 1,
      description: 'Classic dozen red roses with elegant wrapping',
      rating: 4.8
    },
    {
      id: '2',
      name: 'Spring Mixed Bouquet',
      price: 32.99,
      image: 'https://images.unsplash.com/photo-1490750967868-88aa4486c946?w=400&h=400&fit=crop',
      quantity: 2,
      description: 'Colorful spring flowers including tulips and daffodils',
      rating: 4.6
    }
  ])

  const [selectedPerks, setSelectedPerks] = useState<UserSelectedPerk[]>([
    // Start with empty array - perks will only be populated from API response
  ])
  const [perksLoading, setPerksLoading] = useState(false)
  const [perksError, setPerksError] = useState<string | null>(null)

  const [showUSP, setShowUSP] = useState(false)
  const [showApiPanel, setShowApiPanel] = useState(false)
  const [activeTab, setActiveTab] = useState('session')
  const [apiLog, setApiLog] = useState<ApiLog | null>(null)
  const [sessionDetailsLog, setSessionDetailsLog] = useState<ApiLog | null>(null)
  const [sidebarWidth, setSidebarWidth] = useState(480) // Default 480px, wider than before
  const [isResizing, setIsResizing] = useState(false)
  const [isDarkMode, setIsDarkMode] = useState(false)
  const [toasts, setToasts] = useState<Array<{ id: string; message: string; type: 'success' | 'error'; duration?: number; dismissible?: boolean; timer?: ReturnType<typeof setTimeout> | null }>>([])
  const [uspAllOffersChecked, setUspAllOffersChecked] = useState<boolean | null>(null)
  const [apiOffersCount, setApiOffersCount] = useState<number | null>(null)
  const [showTutorial, setShowTutorial] = useState(false)
  const [orderNumber] = useState(() => `USP-${Date.now().toString().slice(-6)}`)
  const [customerInfo] = useState({
    name: 'Sarah Johnson',
    email: 'sarah.j@email.com',
    phone: '+1 (555) 123-4567'
  })

  const subtotal = cartItems.reduce((sum, item) => sum + (item.price * item.quantity), 0)
  const shipping = 12.99
  const tax = subtotal * 0.08
  const total = subtotal + shipping + tax

  // Toast notification helper
  const showToast = (message: string, type: 'success' | 'error', duration: number = 3000, dismissible: boolean = false) => {
    const id = Date.now().toString() + Math.random().toString(36).substr(2, 9)
    
    const removeToast = () => {
      setToasts(prevToasts => prevToasts.filter(toast => toast.id !== id))
    }
    
    // Set timer for auto-dismiss
    const timer = setTimeout(removeToast, duration)
    
    // Add new toast to the array
    setToasts(prevToasts => [...prevToasts, { id, message, type, duration, dismissible, timer }])
  }

  // Dismiss toast manually
  const dismissToast = (id: string) => {
    setToasts(prevToasts => {
      const toast = prevToasts.find(t => t.id === id)
      if (toast && toast.timer) {
        clearTimeout(toast.timer)
      }
      return prevToasts.filter(t => t.id !== id)
    })
  }

  // Tutorial close handler
  const handleTutorialClose = () => {
    setShowTutorial(false)
    // Mark tutorial as seen
    localStorage.setItem('usp_tutorial_seen', 'true')
  }

  useEffect(() => {
    // Check for required credentials from sessionStorage
    const storedApiKey = sessionStorage.getItem('usp_api_key')
    const storedPubUserId = sessionStorage.getItem('usp_pub_user_id')
    
    if (!storedApiKey || !storedPubUserId) {
      // Redirect to setup page if credentials are missing
      navigate('/usp-setup')
      return
    }
    
    setApiKey(storedApiKey)
    setPubUserId(storedPubUserId)
    
    // Log session start confirmation
    const navigationSource = sessionStorage.getItem('usp_navigation_source')
    if (navigationSource === 'setup_page') {
      console.log('✅ New demo session loaded with fresh data')
      // Remove the navigation source flag
      sessionStorage.removeItem('usp_navigation_source')
    }
  }, [navigate])

  // Function to fetch offers and restore the state from stored session
  const fetchOffersAndRestoreState = async (forceNewSession = false) => {
    console.log('🔄 fetchOffersAndRestoreState called with forceNewSession:', forceNewSession);
    
    // Check if there's a stored session ID from previous visits
    const storedSessionId = sessionStorage.getItem('usp_session_id');
    console.log('📋 Stored session ID:', storedSessionId);
    
    if (storedSessionId && !forceNewSession) {
      console.log('♻️ Using existing session:', storedSessionId);
      setSessionId(storedSessionId);
      setShowUSP(true);
      
      // Fetch existing session details instead of creating a new session
      await fetchSessionDetailsAndRestoreState(storedSessionId);
    } else {
      console.log('🆕 Creating new session');
      // If coming from setup or no session ID, create new session
      if (forceNewSession) {
        // Remove any existing session ID
        sessionStorage.removeItem('usp_session_id');
        console.log('🧹 Removed existing session ID');
      }
      
      // Fetch new offers and create a new session
      fetchOffers();
      setShowUSP(true);
    }
  };

  // Function to fetch session details and restore UI state
  const fetchSessionDetailsAndRestoreState = async (sessionIdToUse: string) => {
    setPerksLoading(true);
    
    try {
      // Call the Get Session Details API
      const sessionDetailsResponse = await fetch(
        `https://api.adspostx.com/sdk/v4/usp/session/${sessionIdToUse}.json?pub_user_id=${pubUserId}`, 
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${apiKey}`
          }
        }
      );

      const sessionDetailsData = await sessionDetailsResponse.json();
      
      if (sessionDetailsResponse.ok) {
        // Process the session details to reconstruct the UI state
        if (sessionDetailsData && sessionDetailsData.data) {
          // Check for the campaigns in session.campaigns structure (new API format)
          const sessionData = sessionDetailsData.data.session || sessionDetailsData.data;
          const campaigns = sessionData.campaigns || [];
          const selectedOfferIds = sessionData.selected || [];
          
          // Map campaigns to UI perks format
          const uiPerks = campaigns.map((campaign: any, index: number) => ({
            id: `api-offer-${index}`,
            title: campaign.title || campaign.short_headline || `Offer ${index + 1}`,
            description: campaign.description || campaign.short_description || 'Special offer available',
            value: campaign.advertiser_name ? `Provided by ${campaign.advertiser_name}` : 'Exclusive offer',
            image: campaign.image || (campaign.creatives && campaign.creatives.length > 0 ? 
                   campaign.creatives.find((c: any) => c.is_primary)?.url || campaign.creatives[0].url : null),
            category: getOfferCategory(index),
            icon: getOfferIcon(index),
            selected: selectedOfferIds.includes(campaign.campaign_id),
            sessionId: sessionIdToUse,
            campaignId: campaign.campaign_id,
            offerId: campaign.id
          }));
          
          // Update UI with restored perks
          if (uiPerks.length > 0) {
            setSelectedPerks(uiPerks);
            setShowUSP(true); // Ensure USP section is visible
          } else {
            // If no perks in session, create a new session
            sessionStorage.removeItem('usp_session_id');
            fetchOffers();
            return;
          }
          
          // Check if usp_all_offers_checked is set
          if (sessionData.settings && sessionData.settings.usp_all_offers_checked) {
            setUspAllOffersChecked(true);
          } else {
            setUspAllOffersChecked(false);
          }
          
          setApiOffersCount(campaigns.length);
        } else {
          // If no offers were found, create a new session
          fetchOffers();
        }
      } else {
        showToast('Session expired or invalid. Creating a new session.', 'error');
        
        // If session is invalid or expired, remove from storage and create new one
        sessionStorage.removeItem('usp_session_id');
        fetchOffers();
      }
    } catch (error) {
      showToast('Error retrieving saved session. Creating a new one.', 'error');
      
      // On error, fallback to creating a new session
      sessionStorage.removeItem('usp_session_id');
      fetchOffers();
    } finally {
      setPerksLoading(false);
    }
  };

  // Track navigation source
  const [comingFromSetup, setComingFromSetup] = useState(false);

  useEffect(() => {
    // Check if user is coming from setup page
    const navigationSource = sessionStorage.getItem('usp_navigation_source');
    const hasSeenTutorial = localStorage.getItem('usp_tutorial_seen');
    
    // Show tutorial automatically for first-time users, regardless of navigation source
    if (!hasSeenTutorial) {
      setShowTutorial(true);
      console.log('📚 Showing tutorial for first-time user on USP demo page');
    }
    
    if (navigationSource === 'setup_page') {
      setComingFromSetup(true);
      // Clear the navigation source flag and force new session
      sessionStorage.removeItem('usp_navigation_source');
      sessionStorage.removeItem('usp_session_id'); // Clear old session ID
      console.log('🧹 Cleared session storage for fresh start from setup page');
    }
  }, []);

  useEffect(() => {
    // Show USP immediately after page load (only if credentials are present)
    if (apiKey && pubUserId) {
      fetchOffersAndRestoreState(comingFromSetup);
    }
  }, [apiKey, pubUserId, comingFromSetup])

  // Add a separate effect to handle browser back navigation
  useEffect(() => {
    const handlePopState = () => {
      if (apiKey && pubUserId) {
        // Force restore state when navigating back
        fetchOffersAndRestoreState(false);
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [apiKey, pubUserId])

  useEffect(() => {
    // Initialize theme from localStorage or system preference
    const savedTheme = localStorage.getItem('usp-theme')
    if (savedTheme) {
      setIsDarkMode(savedTheme === 'dark')
    } else {
      // Check system preference
      const systemPrefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches
      setIsDarkMode(systemPrefersDark)
    }
  }, [])

  useEffect(() => {
    // Cleanup timers on unmount
    return () => {
      toasts.forEach(toast => {
        if (toast.timer) {
          clearTimeout(toast.timer)
        }
      })
    }
  }, []) // Remove toasts dependency to prevent interference with active timers

  useEffect(() => {
    // Update HTML class and localStorage when theme changes
    if (isDarkMode) {
      document.documentElement.classList.add('dark')
      localStorage.setItem('usp-theme', 'dark')
    } else {
      document.documentElement.classList.remove('dark')
      localStorage.setItem('usp-theme', 'light')
    }
  }, [isDarkMode])

  // Resize functionality for sidebar
  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault()
    setIsResizing(true)
  }

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizing) return
      
      const newWidth = window.innerWidth - e.clientX
      // Constrain width between 320px and 80% of window width
      const minWidth = 320
      const maxWidth = window.innerWidth * 0.8
      const constrainedWidth = Math.max(minWidth, Math.min(maxWidth, newWidth))
      
      setSidebarWidth(constrainedWidth)
    }

    const handleMouseUp = () => {
      setIsResizing(false)
    }

    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('mouseup', handleMouseUp)
      document.body.style.cursor = 'ew-resize'
      document.body.style.userSelect = 'none'
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
    }
  }, [isResizing])

  const fetchOffers = async () => {
    setPerksLoading(true)
    setPerksError(null)
    
    const requestData = {
      method: 'POST',
      url: 'https://api.adspostx.com/native/v2/offers.json',
      headers: {
        'Content-Type': 'application/json'
      },
      body: {
        pub_user_id: pubUserId,
        country: "us",
        dev: 1
      }
    }

    // Set initial API log with request
    setApiLog({
      request: requestData,
      response: null,
      loading: true,
      error: null
    })

    // Add to shared API logs
    const logId = addApiLog({
      page: 'usp-demo',
      action: 'POST /native/v2/offers.json',
      request: {
        url: `${requestData.url}?api_key=${apiKey}`,
        method: requestData.method,
        headers: requestData.headers,
        body: requestData.body,
        timestamp: new Date().toISOString()
      },
      response: null,
      loading: true,
      error: null
    })

    try {
      const response = await fetch(`${requestData.url}?api_key=${apiKey}`, {
        method: requestData.method,
        headers: requestData.headers,
        body: JSON.stringify(requestData.body)
      })
      
      const responseData = await response.json()
      
      const responseObj = {
        status: response.status,
        statusText: response.statusText,
        headers: Object.fromEntries(response.headers.entries()),
        data: responseData,
        timestamp: new Date().toISOString()
      }

      // Update API log with response
      setApiLog(prevLog => ({
        ...prevLog!,
        response: responseObj,
        loading: false
      }))

      // Update shared API logs
      updateApiLog(logId, {
        response: responseObj,
        loading: false,
        error: null
      })

      // Update perks with data from API response
      // Deep inspect the response structure
      for (const [, value] of Object.entries(responseData)) {
        if (Array.isArray(value) && value.length > 0) {
          // Processing arrays
        }
      }
      
      // Try multiple possible structures
      let foundOffers = false
      
      // Check for "data.offers" array (correct structure)
      if (responseData.data && responseData.data.offers && Array.isArray(responseData.data.offers) && responseData.data.offers.length > 0) {
        // Extract session ID from the API response
        const apiSessionId = responseData.data.session_id || responseData.session_id
        
        // Save session ID to state and sessionStorage for persistence
        if (apiSessionId) {
          sessionStorage.setItem('usp_session_id', apiSessionId);
          setSessionId(apiSessionId);
        } else {
          setSessionId('');
        }
        
        // Extract count from the API response
        const apiCount = responseData.data.count || responseData.count
        setApiOffersCount(apiCount)
        
        const apiPerks = responseData.data.offers.map((offer: any, index: number) => ({
          id: `api-offer-${index}`,
          title: offer.title || offer.name || offer.creative_name || `Offer ${index + 1}`,
          description: offer.description || offer.desc || offer.creative_desc || 'Special offer available',
          value: offer.advertiser_name ? `Provided by ${offer.advertiser_name}` : 'Exclusive offer',
          image: offer.image || offer.creative_image || offer.icon_url || null,
          category: getOfferCategory(index),
          icon: getOfferIcon(index),
          selected: false,
          sessionId: apiSessionId, // Use session ID from API response
          campaignId: offer.campaign_id || offer.campaignId || offer.id,
          offerId: offer.offer_id || offer.offerId || offer.id
        }))
        
        setSelectedPerks(apiPerks.slice(0, 4))
        foundOffers = true
        
        // Track usp_all_offers_checked status
        const uspAllOffersStatus = responseData.data.settings?.usp_all_offers_checked === true
        setUspAllOffersChecked(uspAllOffersStatus)
        
        // Check if usp_all_offers_checked is true and auto-select all offers
        if (uspAllOffersStatus) {
          showToast('All offers have been automatically selected based on the settings in MomentScience Dashboard', 'success', 10000, true)
          
          // Auto-select all offers directly with the apiPerks data
          setTimeout(async () => {
            // Call bulk selection directly with the current data
            await autoSelectAllOffers(apiPerks, apiSessionId)
          }, 1000) // Increased timeout to 1 second
        }
      }        // Check for "offers" array (fallback structure)
      else if (responseData.offers && Array.isArray(responseData.offers) && responseData.offers.length > 0) {
        console.log('✅ Found offers in responseData.offers:', responseData.offers)
        
        // Extract session ID from the API response
        const sessionId = responseData.session_id || responseData.data?.session_id
        console.log('📋 Session ID from API:', sessionId)
        
        // Extract count from the API response
        const apiCount = responseData.count || responseData.data?.count
        console.log('📊 Offers count from API:', apiCount)
        setApiOffersCount(apiCount)
        
        const apiPerks = responseData.offers.map((offer: any, index: number) => ({
          id: `api-offer-${index}`,
          title: offer.title || offer.name || offer.creative_name || `Offer ${index + 1}`,
          description: offer.description || offer.desc || offer.creative_desc || 'Special offer available',
          value: offer.advertiser_name ? `Provided by ${offer.advertiser_name}` : 'Exclusive offer',
          image: offer.image || offer.creative_image || offer.icon_url || null,
          category: getOfferCategory(index),
          icon: getOfferIcon(index),
          selected: false,
          sessionId: sessionId, // Use session ID from API response
          campaignId: offer.campaign_id || offer.campaignId || offer.id,
          offerId: offer.offer_id || offer.offerId || offer.id
        }))
        
        setSelectedPerks(apiPerks.slice(0, 4))
        foundOffers = true
        console.log('✅ Updated perks with offers data')
        
        // Track usp_all_offers_checked status
        const uspAllOffersStatus = responseData.settings?.usp_all_offers_checked === true
        setUspAllOffersChecked(uspAllOffersStatus)
        console.log('📊 Setting uspAllOffersChecked state to:', uspAllOffersStatus)
        
        // Check if usp_all_offers_checked is true and auto-select all offers
        if (uspAllOffersStatus) {
          console.log('🎯 Auto-selecting all offers due to usp_all_offers_checked=true (fallback structure)')
          showToast('All offers have been automatically selected based on the settings in MomentScience Dashboard', 'success', 10000, true)
          
          // Auto-select all offers directly with the apiPerks data
          setTimeout(async () => {
            console.log('🚀 Triggering auto-selection after timeout (fallback structure)')
            await autoSelectAllOffers(apiPerks, sessionId)
          }, 1000)
        }
      }        // Check for "data" array (another fallback structure)
      else if (responseData.data && Array.isArray(responseData.data) && responseData.data.length > 0) {
        console.log('✅ Found offers in responseData.data:', responseData.data)
        
        // Extract session ID from the API response
        const sessionId = responseData.session_id
        console.log('📋 Session ID from API:', sessionId)
        
        // Extract count from the API response
        const apiCount = responseData.count
        console.log('📊 Offers count from API:', apiCount)
        setApiOffersCount(apiCount)
        
        const apiPerks = responseData.data.map((offer: any, index: number) => ({
          id: `api-offer-${index}`,
          title: offer.title || offer.name || offer.creative_name || `Offer ${index + 1}`,
          description: offer.description || offer.desc || offer.creative_desc || 'Special offer available',
          value: offer.advertiser_name ? `Provided by ${offer.advertiser_name}` : 'Exclusive offer',
          image: offer.image || offer.creative_image || offer.icon_url || null,
          category: getOfferCategory(index),
          icon: getOfferIcon(index),
          selected: false,
          sessionId: sessionId, // Use session ID from API response
          campaignId: offer.campaign_id || offer.campaignId || offer.id,
          offerId: offer.offer_id || offer.offerId || offer.id
        }))
        
        setSelectedPerks(apiPerks.slice(0, 4))
        foundOffers = true
        console.log('✅ Updated perks with data array')
        
        // Track usp_all_offers_checked status
        const uspAllOffersStatus = responseData.settings?.usp_all_offers_checked === true
        setUspAllOffersChecked(uspAllOffersStatus)
        console.log('📊 Setting uspAllOffersChecked state to:', uspAllOffersStatus)
        
        // Check if usp_all_offers_checked is true and auto-select all offers
        if (uspAllOffersStatus) {
          console.log('🎯 Auto-selecting all offers due to usp_all_offers_checked=true (data array)')
          showToast('All offers have been automatically selected based on the settings in MomentScience Dashboard', 'success', 10000, true)
          
          // Auto-select all offers directly with the apiPerks data
          setTimeout(async () => {
            console.log('🚀 Triggering auto-selection after timeout (data array)')
            await autoSelectAllOffers(apiPerks, sessionId)
          }, 1000)
        }
      }
      
      // Check for direct array response
      else if (Array.isArray(responseData) && responseData.length > 0) {
        console.log('✅ Response is direct array:', responseData)
        
        // No session ID available in direct array response
        console.log('⚠️ No session ID available for direct array response')
        
        const apiPerks = responseData.map((offer: any, index: number) => ({
          id: `api-offer-${index}`,
          title: offer.title || offer.name || offer.creative_name || `Offer ${index + 1}`,
          description: offer.description || offer.desc || offer.creative_desc || 'Special offer available',
          value: offer.advertiser_name ? `Provided by ${offer.advertiser_name}` : 'Exclusive offer',
          image: offer.image || offer.creative_image || offer.icon_url || null,
          category: getOfferCategory(index),
          icon: getOfferIcon(index),
          selected: false,
          sessionId: offer.session_id || offer.sessionId || null, // May be null for direct array
          campaignId: offer.campaign_id || offer.campaignId || offer.id,
          offerId: offer.offer_id || offer.offerId || offer.id
        }))
        
        setSelectedPerks(apiPerks.slice(0, 4))
        foundOffers = true
        console.log('✅ Updated perks with direct array response')
        
        // Check if usp_all_offers_checked is true and auto-select all offers
        // For direct array response, check if there's a settings object in the response
        if (responseData.some((item: any) => item.settings?.usp_all_offers_checked === true)) {
          console.log('🎯 Auto-selecting all offers due to usp_all_offers_checked=true (direct array)')
          showToast('All offers have been automatically selected based on the settings in MomentScience Dashboard', 'success', 10000, true)
          
          // Auto-select all offers after a brief delay to ensure state is updated
          setTimeout(() => {
            selectAllPerks()
          }, 500)
        }
      }
      
      // Check for any other array fields
      else {
        console.log('🔍 Searching for any array fields in response...')
        for (const [key, value] of Object.entries(responseData)) {
          if (Array.isArray(value) && value.length > 0) {
            console.log(`✅ Found array in ${key}:`, value)
            
            // Extract session ID from the API response
            const sessionId = responseData.session_id || responseData.data?.session_id
            console.log('📋 Session ID from API:', sessionId)
            
            // Extract count from the API response
            const apiCount = responseData.count || responseData.data?.count
            console.log('📊 Offers count from API:', apiCount)
            setApiOffersCount(apiCount)
            
            const apiPerks = value.map((offer: any, index: number) => ({
              id: `api-offer-${index}`,
              title: offer.title || offer.name || offer.creative_name || `${key} ${index + 1}`,
              description: offer.description || offer.desc || offer.creative_desc || `Special ${key} available`,
              value: offer.advertiser_name ? `Provided by ${offer.advertiser_name}` : 'Exclusive offer',
              image: offer.image || offer.creative_image || offer.icon_url || null,
              category: getOfferCategory(index),
              icon: getOfferIcon(index),
              selected: false,
              sessionId: sessionId || offer.session_id || offer.sessionId, // Use session ID from API response
              campaignId: offer.campaign_id || offer.campaignId || offer.id,
              offerId: offer.offer_id || offer.offerId || offer.id
            }))
            
            setSelectedPerks(apiPerks.slice(0, 4))
            foundOffers = true
            console.log(`✅ Updated perks with ${key} array`)
            
            // Track usp_all_offers_checked status
            const uspAllOffersStatus = responseData.settings?.usp_all_offers_checked === true || responseData.data?.settings?.usp_all_offers_checked === true
            setUspAllOffersChecked(uspAllOffersStatus)
            console.log('📊 Setting uspAllOffersChecked state to:', uspAllOffersStatus)
            
            // Check if usp_all_offers_checked is true and auto-select all offers
            if (uspAllOffersStatus) {
              console.log('🎯 Auto-selecting all offers due to usp_all_offers_checked=true')
              showToast('All offers have been automatically selected based on the settings in MomentScience Dashboard', 'success', 10000, true)
              
              // Auto-select all offers after a brief delay to ensure state is updated
              setTimeout(() => {
                selectAllPerks()
              }, 500)
            }
            
            break
          }
        }
      }
      
      if (!foundOffers) {
        console.log('❌ No offers found in any expected structure')
        console.log('Response does not contain usable offer data')
        console.log('🔍 Full response structure for debugging:', JSON.stringify(responseData, null, 2))
        // Keep perks empty - no dummy data, only show real API data
        setSelectedPerks([])
        // Reset usp_all_offers_checked status when no offers found
        setUspAllOffersChecked(null)
        // Reset API offers count when no offers found
        setApiOffersCount(null)
      } else {
        console.log('✅ Successfully processed offers and set state')
        console.log('📊 Final perks count:', selectedPerks.length)
      }

      console.log('MomentScience API Response:', responseData)
    } catch (error) {
      console.error('Error fetching offers:', error)
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch offers'
      setPerksError(errorMessage)
      
      // Keep perks empty on error - no dummy data
      setSelectedPerks([])
      // Reset usp_all_offers_checked status on error
      setUspAllOffersChecked(null)
      // Reset API offers count on error
      setApiOffersCount(null)
      console.log('❌ Error occurred, keeping perks empty')
      
      // Update API log with error
      setApiLog(prevLog => ({
        ...prevLog!,
        loading: false,
        error: errorMessage
      }))

      // Update shared API logs with error
      updateApiLog(logId, {
        response: null,
        loading: false,
        error: errorMessage
      })
    } finally {
      setPerksLoading(false)
    }
  }

  // Don't render anything if credentials are being checked
  if (!apiKey || !pubUserId) {
    return null
  }

  const updateQuantity = (id: string, change: number) => {
    setCartItems(items => items.map(item => 
      item.id === id 
        ? { ...item, quantity: Math.max(1, item.quantity + change) }
        : item
    ))
  }

  const removeItem = (id: string) => {
    setCartItems(items => items.filter(item => item.id !== id))
  }

  const togglePerk = async (perkId: string) => {
    const perk = selectedPerks.find(p => p.id === perkId)
    if (!perk) return

    const historyId = Date.now().toString()
    const timestamp = new Date().toISOString()

    // If selecting the perk (not deselecting)
    if (!perk.selected) {
      // Add to shared API logs
      const selectLogId = addApiLog({
        page: 'usp-demo',
        action: 'POST /sdk/v4/usp/{sessionId}/{campaignId}/select.json',
        request: {
          url: `https://api.adspostx.com/sdk/v4/usp/${perk.sessionId}/${perk.campaignId}/select.json`,
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${apiKey}`
          },
          body: { pub_user_id: pubUserId },
          timestamp: new Date().toISOString()
        },
        response: null,
        loading: true,
        error: null
      })

      try {
        console.log('🎯 Calling Select Offer API for perk:', perk.title)
        console.log('Session ID:', perk.sessionId)
        console.log('Campaign ID:', perk.campaignId)
        console.log('Offer ID:', perk.offerId)

        // Generate curl command for this request
        const selectCurlCommand = `curl -X POST \\
  "https://api.adspostx.com/sdk/v4/usp/${perk.sessionId}/${perk.campaignId}/select.json" \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer ${apiKey}" \\
  -d '${JSON.stringify({ pub_user_id: pubUserId }, null, 2)}'`

        // Call the Select Offer API
        const selectResponse = await fetch(`https://api.adspostx.com/sdk/v4/usp/${perk.sessionId}/${perk.campaignId}/select.json`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${apiKey}`
          },
          body: JSON.stringify({
            pub_user_id: pubUserId
          })
        })

        const selectData = await selectResponse.json()
        console.log('✅ Select Offer API Response:', selectData)
        console.log('Response Status:', selectResponse.status, selectResponse.statusText)
        
        // Update shared API logs with response
        updateApiLog(selectLogId, {
          response: {
            status: selectResponse.status,
            statusText: selectResponse.statusText,
            headers: Object.fromEntries(selectResponse.headers.entries()),
            data: selectData,
            timestamp: new Date().toISOString()
          },
          loading: false,
          error: null
        })
        
        if (selectResponse.ok) {
          console.log('🎉 Offer successfully selected!')
          showToast(`${perk.title} selected successfully!`, 'success', 10000, true)
              // Add to selection history
        setSelectionHistory((prev: SelectionHistoryItem[]) => [...prev, {
          id: historyId,
          timestamp,
          action: 'select',
          perkTitle: perk.title,
          perkId: perk.id,
          campaignId: perk.campaignId,
          status: 'success',
          response: selectData,
          curlCommand: selectCurlCommand
        }])

          // Add to shared selection history
          addSelectionHistory({
            page: 'usp-demo',
            action: 'select',
            perkTitle: perk.title,
            perkId: perk.id,
            campaignId: perk.campaignId,
            status: 'success',
            response: selectData,
            curlCommand: selectCurlCommand
          })
        } else {
          console.error('❌ Select Offer API failed:', selectData)
          showToast('Failed to select offer. Please try again.', 'error', 10000, true)
              // Add to selection history with error
        setSelectionHistory((prev: SelectionHistoryItem[]) => [...prev, {
          id: historyId,
          timestamp,
          action: 'select',
          perkTitle: perk.title,
          perkId: perk.id,
          campaignId: perk.campaignId,
          status: 'error',
          error: selectData.message || 'API request failed',
          curlCommand: selectCurlCommand
        }])

          // Add to shared selection history
          addSelectionHistory({
            page: 'usp-demo',
            action: 'select',
            perkTitle: perk.title,
            perkId: perk.id,
            campaignId: perk.campaignId,
            status: 'error',
            error: selectData.message || 'API request failed',
            curlCommand: selectCurlCommand
          })
          return
        }
      } catch (error) {
        console.error('❌ Error calling Select Offer API:', error)
        const errorMessage = error instanceof Error ? error.message : 'Unknown error'
        showToast('Error selecting offer. Please try again.', 'error')
        
        // Update shared API logs with error
        updateApiLog(selectLogId, {
          response: null,
          loading: false,
          error: errorMessage
        })
        
        // Generate curl command for error case
        const selectCurlCommand = `curl -X POST \\
  "https://api.adspostx.com/sdk/v4/usp/${perk.sessionId}/${perk.campaignId}/select.json" \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer ${apiKey}" \\
  -d '${JSON.stringify({ pub_user_id: pubUserId }, null, 2)}'`
        
        // Add to selection history with error
        setSelectionHistory((prev: SelectionHistoryItem[]) => [...prev, {
          id: historyId,
          timestamp,
          action: 'select',
          perkTitle: perk.title,
          perkId: perk.id,
          campaignId: perk.campaignId,
          status: 'error',
          error: errorMessage,
          curlCommand: selectCurlCommand
        }])

        // Add to shared selection history
        addSelectionHistory({
          page: 'usp-demo',
          action: 'select',
          perkTitle: perk.title,
          perkId: perk.id,
          campaignId: perk.campaignId,
          status: 'error',
          error: errorMessage,
          curlCommand: selectCurlCommand
        })
        return
      }
    } else {
      // If deselecting the perk
      const unselectLogId = addApiLog({
        page: 'usp-demo',
        action: 'POST /sdk/v4/usp/{sessionId}/{campaignId}/unselect.json',
        request: {
          url: `https://api.adspostx.com/sdk/v4/usp/${perk.sessionId}/${perk.campaignId}/unselect.json`,
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${apiKey}`
          },
          body: { pub_user_id: pubUserId },
          timestamp: new Date().toISOString()
        },
        response: null,
        loading: true,
        error: null
      })

      try {
        console.log('🎯 Calling Unselect Offer API for perk:', perk.title)
        console.log('Session ID:', perk.sessionId)
        console.log('Campaign ID:', perk.campaignId)
        console.log('Offer ID:', perk.offerId)

        // Generate curl command for this request
        const unselectCurlCommand = `curl -X POST \\
  "https://api.adspostx.com/sdk/v4/usp/${perk.sessionId}/${perk.campaignId}/unselect.json" \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer ${apiKey}" \\
  -d '${JSON.stringify({ pub_user_id: pubUserId }, null, 2)}'`

        // Call the Unselect Offer API
        const unselectResponse = await fetch(`https://api.adspostx.com/sdk/v4/usp/${perk.sessionId}/${perk.campaignId}/unselect.json`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${apiKey}`
          },
          body: JSON.stringify({
            pub_user_id: pubUserId
          })
        })

        const unselectData = await unselectResponse.json()
        console.log('✅ Unselect Offer API Response:', unselectData)
        console.log('Response Status:', unselectResponse.status, unselectResponse.statusText)
        
        // Update shared API logs with response
        updateApiLog(unselectLogId, {
          response: {
            status: unselectResponse.status,
            statusText: unselectResponse.statusText,
            headers: Object.fromEntries(unselectResponse.headers.entries()),
            data: unselectData,
            timestamp: new Date().toISOString()
          },
          loading: false,
          error: null
        })
        
        if (unselectResponse.ok) {
          console.log('🎉 Offer successfully unselected!')
          showToast(`${perk.title} unselected successfully!`, 'success')
          
          // Add to selection history
          setSelectionHistory((prev: SelectionHistoryItem[]) => [...prev, {
            id: historyId,
            timestamp,
            action: 'unselect',
            perkTitle: perk.title,
            perkId: perk.id,
            campaignId: perk.campaignId,
            status: 'success',
            response: unselectData,
            curlCommand: unselectCurlCommand
          }])

          // Add to shared selection history
          addSelectionHistory({
            page: 'usp-demo',
            action: 'unselect',
            perkTitle: perk.title,
            perkId: perk.id,
            campaignId: perk.campaignId,
            status: 'success',
            response: unselectData,
            curlCommand: unselectCurlCommand
          })
        } else {
          console.error('❌ Unselect Offer API failed:', unselectData)
          showToast('Failed to unselect offer. Please try again.', 'error')
          
          // Add to selection history with error
          setSelectionHistory((prev: SelectionHistoryItem[]) => [...prev, {
            id: historyId,
            timestamp,
            action: 'unselect',
            perkTitle: perk.title,
            perkId: perk.id,
            campaignId: perk.campaignId,
            status: 'error',
            error: unselectData.message || 'API request failed',
            curlCommand: unselectCurlCommand
          }])

          // Add to shared selection history
          addSelectionHistory({
            page: 'usp-demo',
            action: 'unselect',
            perkTitle: perk.title,
            perkId: perk.id,
            campaignId: perk.campaignId,
            status: 'error',
            error: unselectData.message || 'API request failed',
            curlCommand: unselectCurlCommand
          })
          return
        }
      } catch (error) {
        console.error('❌ Error calling Unselect Offer API:', error)
        const errorMessage = error instanceof Error ? error.message : 'Unknown error'
        showToast('Error unselecting offer. Please try again.', 'error')
        
        // Update shared API logs with error
        updateApiLog(unselectLogId, {
          response: null,
          loading: false,
          error: errorMessage
        })
        
        // Generate curl command for error case
        const unselectCurlCommand = `curl -X POST \\
  "https://api.adspostx.com/sdk/v4/usp/${perk.sessionId}/${perk.campaignId}/unselect.json" \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer ${apiKey}" \\
  -d '${JSON.stringify({ pub_user_id: pubUserId }, null, 2)}'`
        
        // Add to selection history with error
        setSelectionHistory((prev: SelectionHistoryItem[]) => [...prev, {
          id: historyId,
          timestamp,
          action: 'unselect',
          perkTitle: perk.title,
          perkId: perk.id,
          campaignId: perk.campaignId,
          status: 'error',
          error: errorMessage,
          curlCommand: unselectCurlCommand
        }])

        // Add to shared selection history
        addSelectionHistory({
          page: 'usp-demo',
          action: 'unselect',
          perkTitle: perk.title,
          perkId: perk.id,
          campaignId: perk.campaignId,
          status: 'error',
          error: errorMessage,
          curlCommand: unselectCurlCommand
        })
        return
      }
    }

    // Update the local state
    setSelectedPerks(perks => perks.map(p => 
      p.id === perkId 
        ? { ...p, selected: !p.selected }
        : p
    ))
  }

  const selectAllPerks = async () => {
    if (selectedPerks.length === 0) return

    const historyId = Date.now().toString()
    const timestamp = new Date().toISOString()

    // Get all campaign IDs from available perks
    const campaignIds = selectedPerks.map(perk => perk.campaignId).filter(id => id)
    
    // Add to shared API logs
    const bulkSelectLogId = addApiLog({
      page: 'usp-demo',
      action: 'PUT /sdk/v4/usp/session/{sessionId}.json (bulk select)',
      request: {
        url: `https://api.adspostx.com/sdk/v4/usp/session/${sessionId}.json?pub_user_id=${pubUserId}`,
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${apiKey}`
        },
        body: { selected: campaignIds },
        timestamp: new Date().toISOString()
      },
      response: null,
      loading: true,
      error: null
    })

    try {
      console.log('🎯 Calling Bulk Selection API to select all offers')
      console.log('Session ID:', sessionId)
      console.log('Available perks:', selectedPerks.map(p => ({ id: p.campaignId, title: p.title })))

      console.log('Campaign IDs to select:', campaignIds)

      // Generate curl command for this request
      const bulkSelectCurlCommand = `curl -X PUT \\
  "https://api.adspostx.com/sdk/v4/usp/session/${sessionId}.json?pub_user_id=${pubUserId}" \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer ${apiKey}" \\
  -d '${JSON.stringify({ selected: campaignIds }, null, 2)}'`

      // Call the Bulk Selection API
      const bulkSelectResponse = await fetch(`https://api.adspostx.com/sdk/v4/usp/session/${sessionId}.json?pub_user_id=${pubUserId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${apiKey}`
        },
        body: JSON.stringify({
          selected: campaignIds
        })
      })

      const bulkSelectData = await bulkSelectResponse.json()
      console.log('✅ Bulk Selection API Response:', bulkSelectData)
      console.log('Response Status:', bulkSelectResponse.status, bulkSelectResponse.statusText)
      
      // Update shared API logs with response
      updateApiLog(bulkSelectLogId, {
        response: {
          status: bulkSelectResponse.status,
          statusText: bulkSelectResponse.statusText,
          headers: Object.fromEntries(bulkSelectResponse.headers.entries()),
          data: bulkSelectData,
          timestamp: new Date().toISOString()
        },
        loading: false,
        error: null
      })
      
      if (bulkSelectResponse.ok) {
        console.log('🎉 All offers successfully selected!')
        showToast('All offers selected successfully!', 'success')
        
        // Add to selection history
        setSelectionHistory((prev: SelectionHistoryItem[]) => [...prev, {
          id: historyId,
          timestamp,
          action: 'bulk_select',
          totalPerks: selectedPerks.length,
          status: 'success',
          response: bulkSelectData,
          curlCommand: bulkSelectCurlCommand
        }])

        // Add to shared selection history
        addSelectionHistory({
          page: 'usp-demo',
          action: 'bulk_select',
          totalPerks: selectedPerks.length,
          status: 'success',
          response: bulkSelectData,
          curlCommand: bulkSelectCurlCommand
        })
        
        // Update local state to mark all perks as selected
        setSelectedPerks(perks => perks.map(p => ({ ...p, selected: true })))
      } else {
        console.error('❌ Bulk Selection API failed:', bulkSelectData)
        showToast('Failed to select all offers. Please try again.', 'error')
        
        // Add to selection history with error
        setSelectionHistory((prev: SelectionHistoryItem[]) => [...prev, {
          id: historyId,
          timestamp,
          action: 'bulk_select',
          totalPerks: selectedPerks.length,
          status: 'error',
          error: bulkSelectData.message || 'API request failed',
          curlCommand: bulkSelectCurlCommand
        }])
      }
    } catch (error) {
      console.error('❌ Error calling Bulk Selection API:', error)
      const errorMessage = error instanceof Error ? error.message : 'Unknown error'
      showToast('Error selecting all offers. Please try again.', 'error')
      
      // Update shared API logs with error
      updateApiLog(bulkSelectLogId, {
        response: null,
        loading: false,
        error: errorMessage
      })
      
      // Generate curl command for error case
      const campaignIds = selectedPerks.map(perk => perk.campaignId).filter(id => id)
      const bulkSelectCurlCommand = `curl -X PUT \\
  "https://api.adspostx.com/sdk/v4/usp/session/${sessionId}.json?pub_user_id=${pubUserId}" \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer ${apiKey}" \\
  -d '${JSON.stringify({ selected: campaignIds }, null, 2)}'`
      
      // Add to selection history with error
      setSelectionHistory((prev: SelectionHistoryItem[]) => [...prev, {
        id: historyId,
        timestamp,
        action: 'bulk_select',
        totalPerks: selectedPerks.length,
        status: 'error',
        error: errorMessage,
        curlCommand: bulkSelectCurlCommand
      }])
    }
  }

  // Auto-select function for when usp_all_offers_checked is true
  const autoSelectAllOffers = async (perks: any[], sessionIdToUse: string) => {
    if (perks.length === 0) return

    const historyId = Date.now().toString()
    const timestamp = new Date().toISOString()

    try {
      console.log('🤖 Auto-selecting all offers via API')
      console.log('Session ID:', sessionIdToUse)
      console.log('Available perks:', perks.map(p => ({ id: p.campaignId, title: p.title })))

      // Get all campaign IDs from available perks
      const campaignIds = perks.map(perk => perk.campaignId).filter(id => id)
      console.log('Campaign IDs to auto-select:', campaignIds)

      // Generate curl command for this request
      const bulkSelectCurlCommand = `curl -X PUT \\
  "https://api.adspostx.com/sdk/v4/usp/session/${sessionIdToUse}.json?pub_user_id=${pubUserId}" \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer ${apiKey}" \\
  -d '${JSON.stringify({ selected: campaignIds }, null, 2)}'`

      // Call the Bulk Selection API
      const bulkSelectResponse = await fetch(`https://api.adspostx.com/sdk/v4/usp/session/${sessionIdToUse}.json?pub_user_id=${pubUserId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${apiKey}`
        },
        body: JSON.stringify({
          selected: campaignIds
        })
      })

      const bulkSelectData = await bulkSelectResponse.json()
      console.log('✅ Auto-selection API Response:', bulkSelectData)
      console.log('Response Status:', bulkSelectResponse.status, bulkSelectResponse.statusText)
      
      if (bulkSelectResponse.ok) {
        console.log('🎉 All offers automatically selected!')
        showToast('All offers automatically selected!', 'success')
        
        // Add to selection history
        setSelectionHistory((prev: SelectionHistoryItem[]) => [...prev, {
          id: historyId,
          timestamp,
          action: 'bulk_select',
          totalPerks: perks.length,
          status: 'success',
          response: bulkSelectData,
          curlCommand: bulkSelectCurlCommand
        }])

        // Add to shared selection history
        addSelectionHistory({
          page: 'usp-demo',
          action: 'bulk_select',
          totalPerks: perks.length,
          status: 'success',
          response: bulkSelectData,
          curlCommand: bulkSelectCurlCommand
        })
        
        // Update local state to mark all perks as selected
        setSelectedPerks(currentPerks => currentPerks.map(p => ({ ...p, selected: true })))
      } else {
        console.error('❌ Auto-selection API failed:', bulkSelectData)
        showToast('Failed to auto-select offers. Please try manual selection.', 'error', 10000, true)
        
        // Add to selection history with error
        setSelectionHistory((prev: SelectionHistoryItem[]) => [...prev, {
          id: historyId,
          timestamp,
          action: 'bulk_select',
          totalPerks: perks.length,
          status: 'error',
          error: bulkSelectData.message || 'API request failed',
          curlCommand: bulkSelectCurlCommand
        }])

        // Add to shared selection history
        addSelectionHistory({
          page: 'usp-demo',
          action: 'bulk_select',
          totalPerks: perks.length,
          status: 'error',
          error: bulkSelectData.message || 'API request failed',
          curlCommand: bulkSelectCurlCommand
        })
      }
    } catch (error) {
      console.error('❌ Error calling Auto-selection API:', error)
      showToast('Error auto-selecting offers. Please try manual selection.', 'error')
      
      // Generate curl command for error case
      const campaignIds = perks.map(perk => perk.campaignId).filter(id => id)
      const bulkSelectCurlCommand = `curl -X PUT \\
  "https://api.adspostx.com/sdk/v4/usp/session/${sessionIdToUse}.json?pub_user_id=${pubUserId}" \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer ${apiKey}" \\
  -d '${JSON.stringify({ selected: campaignIds }, null, 2)}'`
      
      // Add to selection history with error
      setSelectionHistory((prev: SelectionHistoryItem[]) => [...prev, {
        id: historyId,
        timestamp,
        action: 'bulk_select',
        totalPerks: perks.length,
        status: 'error',
        error: error instanceof Error ? error.message : 'Unknown error',
        curlCommand: bulkSelectCurlCommand
      }])

      // Add to shared selection history
      addSelectionHistory({
        page: 'usp-demo',
        action: 'bulk_select',
        totalPerks: perks.length,
        status: 'error',
        error: error instanceof Error ? error.message : 'Unknown error',
        curlCommand: bulkSelectCurlCommand
      })
    }
  }

  const unselectAllPerks = async () => {
    if (selectedPerks.length === 0) return

    const historyId = Date.now().toString()
    const timestamp = new Date().toISOString()

    try {
      console.log('🎯 Calling Bulk Unselection API to unselect all offers')
      console.log('Session ID:', sessionId)
      console.log('Available perks:', selectedPerks.map(p => ({ id: p.campaignId, title: p.title })))

      // Get all campaign IDs from available perks
      const campaignIds = selectedPerks.map(perk => perk.campaignId).filter(id => id)
      console.log('Campaign IDs to unselect:', campaignIds)

      // Generate curl command for this request
      const bulkUnselectCurlCommand = `curl -X PUT \\
  "https://api.adspostx.com/sdk/v4/usp/session/${sessionId}.json?pub_user_id=${pubUserId}" \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer ${apiKey}" \\
  -d '${JSON.stringify({ unselected: campaignIds }, null, 2)}'`

      // Call the Bulk Unselection API
      const bulkUnselectResponse = await fetch(`https://api.adspostx.com/sdk/v4/usp/session/${sessionId}.json?pub_user_id=${pubUserId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${apiKey}`
        },
        body: JSON.stringify({
          unselected: campaignIds
        })
      })

      const bulkUnselectData = await bulkUnselectResponse.json()
      console.log('✅ Bulk Unselection API Response:', bulkUnselectData)
      console.log('Response Status:', bulkUnselectResponse.status, bulkUnselectResponse.statusText)
      
      if (bulkUnselectResponse.ok) {
        console.log('🎉 All offers successfully unselected!')
        showToast('All offers unselected successfully!', 'success')
        
        // Add to selection history
        setSelectionHistory((prev: SelectionHistoryItem[]) => [...prev, {
          id: historyId,
          timestamp,
          action: 'bulk_unselect',
          totalPerks: selectedPerks.length,
          status: 'success',
          response: bulkUnselectData,
          curlCommand: bulkUnselectCurlCommand
        }])

        // Add to shared selection history
        addSelectionHistory({
          page: 'usp-demo',
          action: 'bulk_unselect',
          totalPerks: selectedPerks.length,
          status: 'success',
          response: bulkUnselectData,
          curlCommand: bulkUnselectCurlCommand
        })
        
        // Update local state to mark all perks as unselected
        setSelectedPerks(perks => perks.map(p => ({ ...p, selected: false })))
      } else {
        console.error('❌ Bulk Unselection API failed:', bulkUnselectData)
        showToast('Failed to unselect all offers. Please try again.', 'error', 10000, true)
        
        // Add to selection history with error
        setSelectionHistory((prev: SelectionHistoryItem[]) => [...prev, {
          id: historyId,
          timestamp,
          action: 'bulk_unselect',
          totalPerks: selectedPerks.length,
          status: 'error',
          error: bulkUnselectData.message || 'API request failed',
          curlCommand: bulkUnselectCurlCommand
        }])

        // Add to shared selection history
        addSelectionHistory({
          page: 'usp-demo',
          action: 'bulk_unselect',
          totalPerks: selectedPerks.length,
          status: 'error',
          error: bulkUnselectData.message || 'API request failed',
          curlCommand: bulkUnselectCurlCommand
        })
      }
    } catch (error) {
      console.error('❌ Error calling Bulk Unselection API:', error)
      showToast('Error unselecting all offers. Please try again.', 'error')
      
      // Generate curl command for error case
      const campaignIds = selectedPerks.map(perk => perk.campaignId).filter(id => id)
      const bulkUnselectCurlCommand = `curl -X PUT \\
  "https://api.adspostx.com/sdk/v4/usp/session/${sessionId}.json?pub_user_id=${pubUserId}" \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer ${apiKey}" \\
  -d '${JSON.stringify({ unselected: campaignIds }, null, 2)}'`
      
      // Add to selection history with error
      setSelectionHistory((prev: SelectionHistoryItem[]) => [...prev, {
        id: historyId,
        timestamp,
        action: 'bulk_unselect',
        totalPerks: selectedPerks.length,
        status: 'error',
        error: error instanceof Error ? error.message : 'Unknown error',
        curlCommand: bulkUnselectCurlCommand
      }])

      // Add to shared selection history
      addSelectionHistory({
        page: 'usp-demo',
        action: 'bulk_unselect',
        totalPerks: selectedPerks.length,
        status: 'error',
        error: error instanceof Error ? error.message : 'Unknown error',
        curlCommand: bulkUnselectCurlCommand
      })
    }
  }

  const getSessionDetails = async () => {
    if (!sessionId) {
      showToast('No session ID available', 'error')
      return
    }

    try {
      console.log('🎯 Calling Get Session Details API')
      console.log('Session ID:', sessionId)
      console.log('Pub User ID:', pubUserId)

      // Set loading state for session details
      setSessionDetailsLog({
        request: {
          method: 'GET',
          url: `https://api.adspostx.com/sdk/v4/usp/session/${sessionId}.json`,
          headers: {
            'Authorization': `Bearer ${apiKey}`
          },
          body: `Query: pub_user_id=${pubUserId}`
        },
        response: null,
        loading: true,
        error: null
      })
      
      // Also refresh the UI state from session details
      await fetchSessionDetailsAndRestoreState(sessionId);

      // Call the Get Session Details API
      const sessionDetailsResponse = await fetch(`https://api.adspostx.com/sdk/v4/usp/session/${sessionId}.json?pub_user_id=${pubUserId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${apiKey}`
        }
      })

      const sessionDetailsData = await sessionDetailsResponse.json()
      console.log('✅ Session Details API Response:', sessionDetailsData)
      console.log('Response Status:', sessionDetailsResponse.status, sessionDetailsResponse.statusText)
      
      if (sessionDetailsResponse.ok) {
        console.log('🎉 Session details retrieved successfully!')
        showToast('Session details retrieved successfully!', 'success')
        
        // Update session details log with response
        setSessionDetailsLog({
          request: {
            method: 'GET',
            url: `https://api.adspostx.com/sdk/v4/usp/session/${sessionId}.json`,
            headers: {
              'Authorization': `Bearer ${apiKey}`
            },
            body: `Query: pub_user_id=${pubUserId}`
          },
          response: {
            status: sessionDetailsResponse.status,
            statusText: sessionDetailsResponse.statusText,
            data: sessionDetailsData,
            timestamp: new Date().toISOString()
          },
          loading: false,
          error: null
        })
      } else {
        console.error('❌ Session Details API failed:', sessionDetailsData)
        showToast('Failed to get session details. Please try again.', 'error')
        
        // Update session details log with error response
        setSessionDetailsLog(prevLog => ({
          ...prevLog!,
          response: {
            status: sessionDetailsResponse.status,
            statusText: sessionDetailsResponse.statusText,
            data: sessionDetailsData,
            timestamp: new Date().toISOString()
          },
          loading: false,
          error: 'Session Details API failed'
        }))
      }
    } catch (error) {
      console.error('❌ Error calling Session Details API:', error)
      showToast('Error getting session details. Please try again.', 'error')
      
      // Update session details log with error
      setSessionDetailsLog(prevLog => ({
        ...prevLog!,
        loading: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred'
      }))
    }
  }

  // Secure Checkout: Only navigate to payment confirmation, no API calls
  const handleSecureCheckout = () => {
    navigate('/payment-confirmation', {
      state: {
        orderNumber,
        customerInfo,
        orderTotal: total,
        cartItems: cartItems.map(item => ({
          id: item.id,
          name: item.name,
          price: item.price,
          quantity: item.quantity
        })),
        sessionId,
        pubUserId,
        apiKey
      }
    })
  }

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'shipping': return 'bg-[#233D71]' // Secondary Dark Blue
      case 'discount': return 'bg-[#FE4215]' // Primary Brand Orange
      case 'gift': return 'bg-[#3B82F6]' // Links/Actions Blue
      case 'points': return 'bg-[#FE4215]' // Primary Brand Orange
      default: return 'bg-[#6E6D7A]' // Text Neutral Gray
    }
  }

  const getOfferCategory = (index: number): 'shipping' | 'discount' | 'gift' | 'points' => {
    const categories: ('shipping' | 'discount' | 'gift' | 'points')[] = ['shipping', 'discount', 'gift', 'points']
    return categories[index % categories.length]
  }

  const getOfferIcon = (index: number) => {
    const icons = [Truck, Percent, Gift, Star]
    return icons[index % icons.length]
  }

  const toggleTheme = () => {
    setIsDarkMode(!isDarkMode)
  }

  const formatCurlCommand = (log?: any) => {
    const currentLog = log || apiLog
    if (!currentLog) return ''
    
    const { request } = currentLog
    
    // Handle GET requests (like session details)
    if (request.method === 'GET') {
      const curlCommand = `curl -X ${request.method} \\
  "${request.url}?pub_user_id=${pubUserId}" \\
  -H "Authorization: Bearer ${apiKey}"`
      
      return curlCommand
    }
    
    // Handle POST requests to session endpoints (like wrap session)
    if (request.method === 'POST' && request.url.includes('/session/')) {
      const curlCommand = `curl -X ${request.method} \\
  "${request.url}" \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer ${apiKey}" \\
  -d '${JSON.stringify(request.body, null, 2)}'`
      
      return curlCommand
    }
    
    // Handle POST/PUT requests with api_key in query (like offers, select, unselect)
    const curlCommand = `curl -X ${request.method} \\
  "${request.url}?api_key=${apiKey}" \\
  -H "Content-Type: application/json" \\
  -d '${JSON.stringify(request.body, null, 2)}'`
    
    return curlCommand
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 to-rose-100 dark:from-gray-900 dark:to-gray-800">
      {/* Header */}
      <header className="bg-white dark:bg-gray-800 shadow-sm border-b border-pink-100 dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-[#FE4215] rounded-lg flex items-center justify-center">
                <Heart className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900 dark:text-white">Bloom & Blossom</h1>
                <p className="text-sm text-gray-600 dark:text-gray-400">Premium Flower Delivery</p>
              </div>
            </div>
            <div className="flex items-center space-x-6">
              <div className="hidden lg:flex items-center space-x-4 text-sm">
                <div className="flex items-center space-x-2 text-gray-600 dark:text-gray-400">
                  <Clock className="w-4 h-4" />
                  <span className="font-medium">Session ID:</span>
                  <code className="bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded text-xs">
                    {sessionId || 'Loading...'}
                  </code>
                </div>
                <div className="w-px h-4 bg-gray-300 dark:bg-gray-600"></div>
                <div className="flex items-center space-x-2 text-gray-600 dark:text-gray-400">
                  <span className="font-medium">User ID:</span>
                  <code className="bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded text-xs">
                    {pubUserId}
                  </code>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => {
                    localStorage.removeItem('usp_tutorial_seen')
                    setShowTutorial(true)
                  }}
                  className="text-xs px-2 py-1 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-600 dark:text-gray-400 rounded transition-colors"
                  title="Reset and show tutorial"
                >
                  Show Tutorial
                </button>
                <button
                  onClick={() => setShowApiPanel(!showApiPanel)}
                  className={`flex items-center space-x-2 px-3 py-2 text-sm rounded-lg transition-colors ${
                    showApiPanel
                      ? 'bg-pink-100 dark:bg-pink-900/20 text-pink-700 dark:text-pink-300'
                      : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700'
                  }`}
                >
                  <Code className="w-4 h-4" />
                  <span>API</span>
                  {apiLog?.loading && (
                    <Activity className="w-4 h-4 animate-spin" />
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Cart Items */}
          <div className="lg:col-span-2">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-pink-100 dark:border-gray-700 p-6"
            >
              <div className="flex items-center space-x-3 mb-6">
                <ShoppingCart className="w-6 h-6 text-pink-600" />
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Your Order</h2>
                <span className="bg-pink-100 dark:bg-pink-900 text-pink-800 dark:text-pink-200 px-2 py-1 rounded-full text-sm">
                  {cartItems.reduce((sum, item) => sum + item.quantity, 0)} items
                </span>
              </div>

              <div className="space-y-4">
                {cartItems.map((item) => (
                  <motion.div
                    key={item.id}
                    layout
                    className="flex items-center space-x-4 p-4 border border-gray-100 dark:border-gray-600 rounded-lg hover:border-pink-200 dark:hover:border-pink-700 transition-colors"
                  >
                    <img 
                      src={item.image} 
                      alt={item.name}
                      className="w-20 h-20 object-cover rounded-lg"
                    />
                    
                    <div className="flex-1">
                      <h3 className="font-medium text-gray-900 dark:text-white">{item.name}</h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{item.description}</p>
                      <div className="flex items-center space-x-1 mt-2">
                        <Star className="w-4 h-4 text-yellow-400 fill-current" />
                        <span className="text-sm text-gray-600 dark:text-gray-400">{item.rating}</span>
                      </div>
                    </div>

                    <div className="flex items-center space-x-3">
                      <div className="flex items-center space-x-2 bg-gray-50 dark:bg-gray-700 rounded-lg p-1">
                        <button
                          onClick={() => updateQuantity(item.id, -1)}
                          className="p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded"
                        >
                          <Minus className="w-4 h-4" />
                        </button>
                        <span className="w-8 text-center font-medium">{item.quantity}</span>
                        <button
                          onClick={() => updateQuantity(item.id, 1)}
                          className="p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded"
                        >
                          <Plus className="w-4 h-4" />
                        </button>
                      </div>
                      
                      <div className="text-right">
                        <p className="font-semibold text-gray-900 dark:text-white">
                          ${(item.price * item.quantity).toFixed(2)}
                        </p>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          ${item.price.toFixed(2)} each
                        </p>
                      </div>

                      <button
                        onClick={() => removeItem(item.id)}
                        className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            {/* User Selected Perks Section */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: showUSP ? 1 : 0, y: showUSP ? 0 : 20 }}
              transition={{ duration: 0.3 }}
              className={`mt-6 bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-pink-100 dark:border-gray-700 p-6 ${
                showUSP ? 'ring-2 ring-pink-500 ring-opacity-20' : ''
              }`}
            >
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-[#FE4215] rounded-lg flex items-center justify-center">
                    <Gift className="w-5 h-5 text-white" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Choose Your Perks</h3>
                  <span className="bg-[#FE4215] text-white px-3 py-1 rounded-lg text-sm font-medium">
                    Exclusive Offer
                  </span>
                </div>
                
                {selectedPerks.length > 0 && (
                  <div className="flex items-center space-x-2">
                    {selectedPerks.every(perk => perk.selected) ? (
                      // Show "Unselect All" when all perks are selected
                      <button
                        onClick={unselectAllPerks}
                        className="flex items-center space-x-2 px-3 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium transition-colors"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                        <span>Unselect All</span>
                      </button>
                    ) : (
                      // Show "Select All" when not all perks are selected
                      <button
                        onClick={selectAllPerks}
                        className="flex items-center space-x-2 px-3 py-2 bg-[#233D71] hover:bg-[#1E3460] text-white rounded-lg text-sm font-medium transition-colors"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span>Select All</span>
                      </button>
                    )}
                  </div>
                )}
              </div>

              <p className="text-gray-600 dark:text-gray-400 mb-6">
                🎉 Your order unlocked exclusive perks! Choose your favorites and receive them via email with your receipt.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {perksLoading ? (
                  <div className="col-span-2 flex flex-col items-center justify-center p-8 text-center">
                    <Activity className="w-16 h-16 text-[#FE4215] animate-spin mb-4" />
                    <h4 className="text-lg font-medium text-gray-500 dark:text-gray-400 mb-2">
                      Loading Perks...
                    </h4>
                    <p className="text-sm text-gray-400 dark:text-gray-500">
                      Fetching exclusive offers from MomentScience API
                    </p>
                  </div>
                ) : selectedPerks.length > 0 ? (
                  selectedPerks.map((perk) => {
                    const IconComponent = perk.icon
                    return (
                      <motion.div
                        key={perk.id}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => togglePerk(perk.id)}
                        className={`relative p-4 border-2 rounded-xl cursor-pointer transition-all duration-300 ${
                          perk.selected
                            ? 'border-[#FE4215] bg-[#FE4215]/10 dark:bg-[#FE4215]/20'
                            : 'border-gray-200 dark:border-gray-600 hover:border-[#FE4215]/50 dark:hover:border-[#FE4215]/50'
                        }`}
                      >
                        <div className="flex items-start space-x-3">
                          {perk.image ? (
                            <img 
                              src={perk.image} 
                              alt={perk.title}
                              className="w-10 h-10 object-cover rounded-lg flex-shrink-0"
                              onError={(e) => {
                                // Fallback to icon if image fails to load
                                e.currentTarget.style.display = 'none'
                                e.currentTarget.nextElementSibling?.classList.remove('hidden')
                              }}
                            />
                          ) : null}
                          <div className={`w-10 h-10 ${getCategoryColor(perk.category)} rounded-lg flex items-center justify-center flex-shrink-0 ${perk.image ? 'hidden' : ''}`}>
                            <IconComponent className="w-5 h-5 text-white" />
                          </div>
                          
                          <div className="flex-1">
                            <div className="flex items-center justify-between mb-2">
                              <h4 className="font-medium text-gray-900 dark:text-white">{perk.title}</h4>
                              {perk.selected && (
                                <motion.div
                                  initial={{ scale: 0 }}
                                  animate={{ scale: 1 }}
                                  className="w-6 h-6 min-w-[1.5rem] min-h-[1.5rem] bg-[#FE4215] rounded-full flex items-center justify-center flex-shrink-0 aspect-square"
                                >
                                  <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                  </svg>
                                </motion.div>
                              )}
                            </div>
                            <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">{perk.description}</p>
                            <p className="text-sm font-medium text-[#FE4215] dark:text-[#FE4215]">{perk.value}</p>
                          </div>
                        </div>
                      </motion.div>
                    )
                  })
                ) : (
                  <div className="col-span-2 flex flex-col items-center justify-center p-8 text-center">
                    <Gift className="w-16 h-16 text-gray-300 dark:text-gray-600 mb-4" />
                    <h4 className="text-lg font-medium text-gray-500 dark:text-gray-400 mb-2">
                      No Perks Available
                    </h4>
                    <p className="text-sm text-gray-400 dark:text-gray-500">
                      {perksError ? 'API request failed' : 'No offers returned from MomentScience API'}
                    </p>
                    {perksError && (
                      <p className="text-xs text-red-500 mt-2">
                        Error: {perksError}
                      </p>
                    )}
                    <button
                      onClick={fetchOffers}
                      className="mt-4 px-4 py-2 bg-[#FE4215] hover:bg-[#E53910] text-white rounded-lg text-sm font-medium transition-colors"
                    >
                      Retry API Call
                    </button>
                  </div>
                )}
              </div>

              <div className="mt-6 p-4 bg-[#F9F9F9] dark:bg-gray-800 rounded-lg border border-[#FE4215]/20">
                <div className="flex items-center space-x-2">
                  <Gift className="w-5 h-5 text-[#FE4215]" />
                  <span className="text-sm font-medium text-[#233D71] dark:text-white">
                    {selectedPerks.filter(p => p.selected).length} of {apiOffersCount || selectedPerks.length} perks selected
                  </span>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-pink-100 dark:border-gray-700 p-6 sticky top-6"
            >
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">Order Summary</h3>
              
              {/* Customer Info */}
              <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <h4 className="font-medium text-gray-900 dark:text-white mb-3">Delivery Details</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center space-x-2">
                    <span className="text-gray-600 dark:text-gray-400">Name:</span>
                    <span className="text-gray-900 dark:text-white">{customerInfo.name}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <MapPin className="w-4 h-4 text-gray-400" />
                    <span className="text-gray-600 dark:text-gray-400">123 Garden St, Flower City, FC 12345</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Clock className="w-4 h-4 text-gray-400" />
                    <span className="text-gray-600 dark:text-gray-400">Today, 2:00 PM - 4:00 PM</span>
                  </div>
                </div>
              </div>

              {/* Pricing */}
              <div className="space-y-3 border-b border-gray-200 dark:border-gray-600 pb-4 mb-4">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600 dark:text-gray-400">Subtotal</span>
                  <span className="text-gray-900 dark:text-white">${subtotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600 dark:text-gray-400">Shipping</span>
                  <span className="text-gray-900 dark:text-white">${shipping.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600 dark:text-gray-400">Tax</span>
                  <span className="text-gray-900 dark:text-white">${tax.toFixed(2)}</span>
                </div>
              </div>

              <div className="flex justify-between text-lg font-semibold text-gray-900 dark:text-white mb-6">
                <span>Total</span>
                <span>${total.toFixed(2)}</span>
              </div>

              {/* Checkout Button */}
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={handleSecureCheckout}
                className="w-full bg-[#FE4215] hover:bg-[#E53910] text-white py-4 rounded-lg font-semibold flex items-center justify-center space-x-2 transition-all duration-300 shadow-lg hover:shadow-xl"
              >
                <Lock className="w-5 h-5" />
                <span>Continue to Payment</span>
              </motion.button>

              <div className="mt-4 flex items-center justify-center space-x-2 text-sm text-gray-500 dark:text-gray-400">
                <CreditCard className="w-4 h-4" />
                <span>SSL Encrypted • 100% Secure</span>
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* API Panel Sidebar */}
      <motion.div
        initial={{ x: '100%' }}
        animate={{ x: showApiPanel ? 0 : '100%' }}
        transition={{ type: 'spring', damping: 25, stiffness: 200 }}
        className="fixed top-0 right-0 h-full bg-white dark:bg-gray-900 shadow-2xl border-l border-gray-200 dark:border-gray-700 z-50 overflow-hidden"
        style={{ width: `${sidebarWidth}px` }}
      >
        {/* Resize Handle */}
        <div
          className={`absolute left-0 top-0 w-1 h-full bg-gray-300 dark:bg-gray-600 hover:bg-pink-500 dark:hover:bg-pink-400 cursor-ew-resize transition-colors z-10 ${
            isResizing ? 'bg-pink-500 dark:bg-pink-400' : ''
          }`}
          onMouseDown={handleMouseDown}
          title="Drag to resize sidebar"
        />
        
        {/* Resize Handle Visual Indicator */}
        <div
          className={`absolute left-0.5 top-1/2 transform -translate-y-1/2 w-0.5 h-8 bg-white dark:bg-gray-800 rounded-full opacity-60 pointer-events-none ${
            isResizing ? 'opacity-100' : ''
          }`}
        />

        <div className="flex flex-col h-full">
          {/* Panel Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center space-x-2">
              <Code className="w-5 h-5 text-pink-600" />
              <h3 className="font-semibold text-gray-900 dark:text-white">API Monitor</h3>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setShowApiPanel(false)}
                className="p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded"
              >
                <Eye className="w-5 h-5 text-gray-500" />
              </button>
            </div>
          </div>

          {/* Tab Navigation */}
          <div className="flex border-b border-gray-200 dark:border-gray-700">
            <button
              onClick={() => setActiveTab('session')}
              className={`flex-1 px-3 py-3 text-sm font-medium transition-colors ${
                activeTab === 'session'
                  ? 'bg-pink-50 dark:bg-pink-900/20 text-pink-600 dark:text-pink-400 border-b-2 border-pink-500'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
              }`}
            >
              Session API
            </button>
            <button
              onClick={() => setActiveTab('general')}
              className={`flex-1 px-3 py-3 text-sm font-medium transition-colors ${
                activeTab === 'general'
                  ? 'bg-pink-50 dark:bg-pink-900/20 text-pink-600 dark:text-pink-400 border-b-2 border-pink-500'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
              }`}
            >
              Moments API
            </button>
            <button
              onClick={() => setActiveTab('history')}
              className={`flex-1 px-3 py-3 text-sm font-medium transition-colors ${
                activeTab === 'history'
                  ? 'bg-pink-50 dark:bg-pink-900/20 text-pink-600 dark:text-pink-400 border-b-2 border-pink-500'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
              }`}
            >
              Selection History
            </button>
          </div>

          {/* Tab Content */}
          <div className="flex-1 overflow-y-auto p-4">
            {activeTab === 'session' ? (
              <div className="space-y-4">
                {/* Session API Tab Content */}
                
                {/* Illustration/Guide when no session data is available */}
                {!sessionDetailsLog && (
                  <div className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-xl p-6 border border-blue-200 dark:border-blue-800">
                    <div className="text-center mb-4">
                      <div className="relative mx-auto w-16 h-16 mb-4">
                        {/* Animated pointer illustration */}
                        <div className="absolute inset-0 bg-blue-500 rounded-full opacity-20 animate-ping"></div>
                        <div className="relative w-16 h-16 bg-blue-500 rounded-full flex items-center justify-center">
                          <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.121 2.122" />
                          </svg>
                        </div>
                      </div>
                      <h4 className="text-lg font-semibold text-blue-900 dark:text-blue-100 mb-2">
                        Discover Session Insights
                      </h4>
                      <p className="text-blue-700 dark:text-blue-300 text-sm mb-4">
                        Click the button below to fetch detailed information about your current session, including campaigns, device info, and user statistics.
                      </p>
                      
                      {/* Feature highlights */}
                      <div className="grid grid-cols-2 gap-3 text-left mb-4">
                        <div className="flex items-center space-x-2 text-xs text-blue-600 dark:text-blue-400">
                          <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                          <span>Campaign Analytics</span>
                        </div>
                        <div className="flex items-center space-x-2 text-xs text-blue-600 dark:text-blue-400">
                          <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                          <span>Device Information</span>
                        </div>
                        <div className="flex items-center space-x-2 text-xs text-blue-600 dark:text-blue-400">
                          <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                          <span>Selection Statistics</span>
                        </div>
                        <div className="flex items-center space-x-2 text-xs text-blue-600 dark:text-blue-400">
                          <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                          <span>Location Data</span>
                        </div>
                      </div>
                      
                      {/* Animated arrow pointing down */}
                      <div className="flex justify-center">
                        <svg className="w-6 h-6 text-blue-500 animate-bounce" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                        </svg>
                      </div>
                    </div>
                  </div>
                )}

                <div className="text-center">
                  <button
                    onClick={getSessionDetails}
                    disabled={!sessionId}
                    className="w-full flex items-center justify-center space-x-2 px-4 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white rounded-lg transition-colors shadow-lg hover:shadow-xl"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <span>Get Current Session Details</span>
                    {!sessionDetailsLog && (
                      <span className="ml-2 px-2 py-1 bg-blue-500 text-blue-100 text-xs rounded-full">
                        Try it!
                      </span>
                    )}
                  </button>
                </div>

                {/* Session Details Response */}
                {sessionDetailsLog && (
                  <div className="space-y-4">
                    {/* Session Statistics - Only show when we have a successful response with session data */}
                    {sessionDetailsLog.response && 
                     sessionDetailsLog.response.status >= 200 && 
                     sessionDetailsLog.response.status < 300 && 
                     sessionDetailsLog.response.data?.data?.session && (
                      <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                        <h4 className="font-medium text-gray-900 dark:text-white mb-3">Session Statistics</h4>
                        <div className="grid grid-cols-2 gap-3">
                          {/* Total Campaigns */}
                          <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3 border border-blue-200 dark:border-blue-800">
                            <div className="text-blue-600 dark:text-blue-400 text-xs font-medium">Total Campaigns</div>
                            <div className="text-blue-900 dark:text-blue-100 text-lg font-bold">
                              {sessionDetailsLog.response.data.data.session.campaigns?.length || 0}
                            </div>
                          </div>
                          
                          {/* Selected Campaigns */}
                          <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-3 border border-green-200 dark:border-green-800">
                            <div className="text-green-600 dark:text-green-400 text-xs font-medium">Selected</div>
                            <div className="text-green-900 dark:text-green-100 text-lg font-bold">
                              {sessionDetailsLog.response.data.data.session.selected?.length || 0}
                            </div>
                          </div>
                          
                          {/* Device Info */}
                          <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-3 border border-purple-200 dark:border-purple-800">
                            <div className="text-purple-600 dark:text-purple-400 text-xs font-medium">Device</div>
                            <div className="text-purple-900 dark:text-purple-100 text-sm font-medium capitalize">
                              {sessionDetailsLog.response.data.data.session.device || 'Unknown'} / {sessionDetailsLog.response.data.data.session.os || 'Unknown'}
                            </div>
                          </div>
                          
                          {/* Browser */}
                          <div className="bg-orange-50 dark:bg-orange-900/20 rounded-lg p-3 border border-orange-200 dark:border-orange-800">
                            <div className="text-orange-600 dark:text-orange-400 text-xs font-medium">Browser</div>
                            <div className="text-orange-900 dark:text-orange-100 text-sm font-medium capitalize">
                              {sessionDetailsLog.response.data.data.session.browser || 'Unknown'}
                            </div>
                          </div>
                          
                          {/* Location */}
                          <div className="col-span-2 bg-gray-50 dark:bg-gray-800 rounded-lg p-3 border border-gray-200 dark:border-gray-700">
                            <div className="text-gray-600 dark:text-gray-400 text-xs font-medium mb-1">Location</div>
                            <div className="text-gray-900 dark:text-gray-100 text-sm">
                              {sessionDetailsLog.response.data.data.session.sdk_payload?._location ? (
                                `${sessionDetailsLog.response.data.data.session.sdk_payload._location.city || 'Unknown'}, ${sessionDetailsLog.response.data.data.session.sdk_payload._location.state || 'Unknown'}, ${sessionDetailsLog.response.data.data.session.sdk_payload._location.country || 'Unknown'}`
                              ) : 'Location not available'}
                            </div>
                          </div>
                          
                          {/* Campaign Advertisers */}
                          {sessionDetailsLog.response.data.data.session.campaigns?.length > 0 && (
                            <div className="col-span-2 bg-pink-50 dark:bg-pink-900/20 rounded-lg p-3 border border-pink-200 dark:border-pink-800">
                              <div className="text-pink-600 dark:text-pink-400 text-xs font-medium mb-2">Advertisers</div>
                              <div className="flex flex-wrap gap-1">
                                {[...new Set(sessionDetailsLog.response.data.data.session.campaigns.map((campaign: any) => campaign.advertiser_name).filter(Boolean))].slice(0, 4).map((advertiser: unknown, index: number) => (
                                  <span key={index} className="text-xs bg-pink-100 dark:bg-pink-800 text-pink-800 dark:text-pink-200 px-2 py-1 rounded">
                                    {String(advertiser)}
                                  </span>
                                ))}
                                {[...new Set(sessionDetailsLog.response.data.data.session.campaigns.map((campaign: any) => campaign.advertiser_name).filter(Boolean))].length > 4 && (
                                  <span className="text-xs text-pink-600 dark:text-pink-400">
                                    +{[...new Set(sessionDetailsLog.response.data.data.session.campaigns.map((campaign: any) => campaign.advertiser_name).filter(Boolean))].length - 4} more
                                  </span>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                      <h4 className="font-medium text-gray-900 dark:text-white mb-3">Session Details Response</h4>
                      
                      {sessionDetailsLog.loading ? (
                        <div className="flex items-center justify-center p-8">
                          <div className="flex items-center space-x-2">
                            <Activity className="w-5 h-5 animate-spin text-pink-600" />
                            <span className="text-gray-600 dark:text-gray-400">Loading session details...</span>
                          </div>
                        </div>
                      ) : sessionDetailsLog.error ? (
                        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
                          <div className="text-red-600 dark:text-red-400 font-medium mb-1">Error</div>
                          <div className="text-red-700 dark:text-red-300 text-sm">{sessionDetailsLog.error}</div>
                        </div>
                      ) : sessionDetailsLog.response ? (
                        <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3 space-y-3">
                          <div className="flex items-center space-x-2 text-sm">
                            <span className={`px-2 py-1 rounded text-xs font-medium ${
                              sessionDetailsLog.response.status >= 200 && sessionDetailsLog.response.status < 300
                                ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400'
                                : 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400'
                            }`}>
                              {sessionDetailsLog.response.status}
                            </span>
                            <span className="text-gray-600 dark:text-gray-400">
                              {sessionDetailsLog.response.statusText}
                            </span>
                          </div>
                          
                          <div className="text-xs">
                            <div className="text-gray-500 dark:text-gray-400 mb-1">Response Data:</div>
                            <div className="bg-white dark:bg-gray-900 rounded p-2 font-mono max-h-64 overflow-y-auto">
                              <pre className="whitespace-pre-wrap text-xs">
                                {JSON.stringify(sessionDetailsLog.response.data, null, 2)}
                              </pre>
                            </div>
                          </div>

                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            Timestamp: {new Date(sessionDetailsLog.response.timestamp).toLocaleString()}
                          </div>
                        </div>
                      ) : null}
                    </div>

                    {/* Session Details cURL Command */}
                    <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                      <h4 className="font-medium text-gray-900 dark:text-white mb-3">cURL Command</h4>
                      <div className="bg-gray-900 dark:bg-black rounded-lg p-3">
                        <pre className="text-green-400 text-xs font-mono whitespace-pre-wrap">
                          {`curl -X GET \\
  "https://api.adspostx.com/sdk/v4/usp/session/${sessionId}.json?pub_user_id=${pubUserId}" \\
  -H "Authorization: Bearer ${apiKey}"`}
                        </pre>
                      </div>
                    </div>
                  </div>
                )}

                {!sessionDetailsLog && (
                  <div className="flex items-center justify-center h-32">
                    <div className="text-center">
                      <Code className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                      <p className="text-gray-500 dark:text-gray-400">No session details fetched yet</p>
                    </div>
                  </div>
                )}
              </div>
            ) : activeTab === 'general' ? (
              <div className="space-y-6">
                {/* USP Auto-Selection Status Section */}
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-lg p-4 border border-blue-200 dark:border-blue-800">
                  <div className="flex items-center space-x-2 mb-3">
                    <div className={`w-3 h-3 rounded-full ${
                      uspAllOffersChecked === true 
                        ? 'bg-green-500' 
                        : uspAllOffersChecked === false 
                          ? 'bg-yellow-500' 
                          : 'bg-gray-400'
                    }`}></div>
                    <h4 className="font-medium text-gray-900 dark:text-white">Auto-Selection Status</h4>
                  </div>
                  
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600 dark:text-gray-400">usp_all_offers_checked:</span>
                      <span className={`font-mono px-2 py-1 rounded text-xs ${
                        uspAllOffersChecked === true 
                          ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400' 
                          : uspAllOffersChecked === false 
                            ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400' 
                            : 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400'
                      }`}>
                        {uspAllOffersChecked === null ? 'not set' : uspAllOffersChecked.toString()}
                      </span>
                    </div>
                    
                    <div className="mt-2 p-3 bg-white dark:bg-gray-800 rounded border">
                      <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Impact:</div>
                      <div className="text-sm text-gray-700 dark:text-gray-300">
                        {uspAllOffersChecked === true ? (
                          <div className="flex items-start space-x-2">
                            <div className="w-1.5 h-1.5 bg-green-500 rounded-full mt-2 flex-shrink-0"></div>
                            <div>
                              <strong className="text-green-700 dark:text-green-400">Auto-selection enabled</strong>
                              <br />
                              All available offers are automatically selected when the session loads. The bulk selection API is called immediately after offers are retrieved. This behavior is controlled by the auto-selection setting configured in your MomentScience account dashboard.
                            </div>
                          </div>
                        ) : uspAllOffersChecked === false ? (
                          <div className="flex items-start space-x-2">
                            <div className="w-1.5 h-1.5 bg-yellow-500 rounded-full mt-2 flex-shrink-0"></div>
                            <div>
                              <strong className="text-yellow-700 dark:text-yellow-400">Manual selection required</strong>
                              <br />
                              Users must manually select individual offers or use the "Select All" button to choose their preferred offers.
                            </div>
                          </div>
                        ) : (
                          <div className="flex items-start space-x-2">
                            <div className="w-1.5 h-1.5 bg-gray-500 rounded-full mt-2 flex-shrink-0"></div>
                            <div>
                              <strong className="text-gray-700 dark:text-gray-400">Status unknown</strong>
                              <br />
                              No API response received yet, or the usp_all_offers_checked setting was not found in the response.
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Moments API Tab Content */}
                {(() => {
                  const uspPageLogs = getLogsByPage('usp-demo')
                  return uspPageLogs.length > 0 ? (
                    <div className="space-y-6">
                      {uspPageLogs.map((log, index) => (
                        <div key={log.id} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                          <div className="flex items-center justify-between mb-3">
                            <h4 className="font-medium text-gray-900 dark:text-white">
                              API Call #{uspPageLogs.length - index}
                            </h4>
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                              {new Date(log.timestamp).toLocaleString()}
                            </span>
                          </div>
                          
                          {/* Request Section */}
                          <div className="mb-4">
                            <h5 className="font-medium text-gray-800 dark:text-gray-200 mb-2 text-sm">Request</h5>
                            
                            <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3 space-y-2">
                              <div className="text-sm">
                                <span className="font-medium text-pink-600">{log.request.method}</span>
                                <span className="ml-2 text-gray-600 dark:text-gray-400 font-mono text-xs">
                                  {log.request.url}
                                </span>
                              </div>
                              
                              <div className="text-xs">
                                <div className="text-gray-500 dark:text-gray-400 mb-1">Headers:</div>
                                <div className="bg-white dark:bg-gray-900 rounded p-2 font-mono">
                                  {JSON.stringify(log.request.headers, null, 2)}
                                </div>
                              </div>

                              <div className="text-xs">
                                <div className="text-gray-500 dark:text-gray-400 mb-1">Body:</div>
                                <div className="bg-white dark:bg-gray-900 rounded p-2 font-mono">
                                  {JSON.stringify(log.request.body, null, 2)}
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Response Section */}
                          <div className="mb-4">
                            <h5 className="font-medium text-gray-800 dark:text-gray-200 mb-2 text-sm">Response</h5>
                            
                            {log.loading ? (
                              <div className="flex items-center justify-center p-8">
                                <div className="flex items-center space-x-2">
                                  <Activity className="w-5 h-5 animate-spin text-pink-600" />
                                  <span className="text-gray-600 dark:text-gray-400">Loading...</span>
                                </div>
                              </div>
                            ) : log.error ? (
                              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
                                <div className="text-red-600 dark:text-red-400 font-medium mb-1">Error</div>
                                <div className="text-red-700 dark:text-red-300 text-sm">{log.error}</div>
                              </div>
                            ) : log.response ? (
                              <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3 space-y-2">
                                <div className="flex items-center space-x-2 text-sm">
                                  <span className={`px-2 py-1 rounded text-xs font-medium ${
                                    log.response.status >= 200 && log.response.status < 300
                                      ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400'
                                      : 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400'
                                  }`}>
                                    {log.response.status}
                                  </span>
                                  <span className="text-gray-600 dark:text-gray-400">
                                    {log.response.statusText}
                                  </span>
                                </div>
                                
                                <div className="text-xs">
                                  <div className="text-gray-500 dark:text-gray-400 mb-1">
                                    Response Data:
                                  </div>
                                  <div className="bg-white dark:bg-gray-900 rounded p-2 font-mono max-h-64 overflow-y-auto">
                                    <pre className="whitespace-pre-wrap text-xs">
                                      {JSON.stringify(log.response.data, null, 2)}
                                    </pre>
                                  </div>
                                </div>

                                <div className="text-xs text-gray-500 dark:text-gray-400">
                                  Timestamp: {new Date(log.response.timestamp).toLocaleString()}
                                </div>
                              </div>
                            ) : null}
                          </div>

                          {/* cURL Command Section */}
                          <div>
                            <h5 className="font-medium text-gray-800 dark:text-gray-200 mb-2 text-sm">cURL Command</h5>
                            <div className="bg-gray-900 dark:bg-black rounded-lg p-3">
                              <pre className="text-green-400 text-xs font-mono whitespace-pre-wrap">
                                {formatCurlCommand(log)}
                              </pre>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="flex items-center justify-center h-32">
                      <div className="text-center">
                        <Code className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                        <p className="text-gray-500 dark:text-gray-400">No API calls yet</p>
                      </div>
                    </div>
                  )
                })()}
              </div>
            ) : (
              <div className="space-y-4">
                {/* Selection History Tab Content */}
                <div className="flex items-center justify-between">
                  <h4 className="font-medium text-gray-900 dark:text-white">Selection History</h4>
                  {getSelectionHistoryByPage('usp-demo').length > 0 && (
                    <button
                      onClick={() => clearSelectionHistory('usp-demo')}
                      className="text-sm text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-200"
                    >
                      Clear History
                    </button>
                  )}
                </div>

                {getSelectionHistoryByPage('usp-demo').length > 0 ? (
                  <div className="space-y-3">
                    {getSelectionHistoryByPage('usp-demo').slice().reverse().map((entry) => (
                      <div
                        key={entry.id}
                        className={`border rounded-lg p-3 ${
                          entry.status === 'success'
                            ? 'border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20'
                            : 'border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20'
                        }`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center space-x-2">
                            <span className={`px-2 py-1 rounded text-xs font-medium ${
                              entry.action === 'select'
                                ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400'
                                : entry.action === 'unselect'
                                ? 'bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-400'
                                : entry.action === 'bulk_select'
                                ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-400'
                                : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-400'
                            }`}>
                              {entry.action.replace('_', ' ').toUpperCase()}
                            </span>
                            <span className={`px-2 py-1 rounded text-xs font-medium ${
                              entry.status === 'success'
                                ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400'
                                : 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400'
                            }`}>
                              {entry.status.toUpperCase()}
                            </span>
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            {new Date(entry.timestamp).toLocaleString()}
                          </div>
                        </div>

                        <div className="text-sm text-gray-900 dark:text-white">
                          {entry.action === 'bulk_select' || entry.action === 'bulk_unselect' ? (
                            <span>
                              <strong>{entry.action === 'bulk_select' ? 'Selected' : 'Unselected'}</strong> {entry.totalPerks} perk{entry.totalPerks !== 1 ? 's' : ''}
                            </span>
                          ) : (
                            <span>
                              <strong>{entry.action === 'select' ? 'Selected' : 'Unselected'}</strong> "{entry.perkTitle}"
                            </span>
                          )}
                        </div>

                        {entry.error && (
                          <div className="text-sm text-red-600 dark:text-red-400 mt-2">
                            Error: {entry.error}
                          </div>
                        )}

                        {entry.perkId && (
                          <div className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                            Campaign ID: {entry.campaignId}
                          </div>
                        )}

                        {entry.curlCommand && (
                          <details className="mt-2">
                            <summary className="text-xs text-gray-500 dark:text-gray-400 cursor-pointer hover:text-gray-700 dark:hover:text-gray-300">
                              View cURL Command
                            </summary>
                            <div className="mt-2 bg-gray-100 dark:bg-gray-800 rounded p-3 font-mono">
                              <pre className="whitespace-pre-wrap text-xs text-gray-800 dark:text-gray-200 overflow-x-auto">
                                {entry.curlCommand}
                              </pre>
                              <button
                                onClick={() => {
                                  navigator.clipboard.writeText(entry.curlCommand!)
                                  showToast('cURL command copied to clipboard!', 'success')
                                }}
                                className="mt-2 px-2 py-1 bg-pink-500 hover:bg-pink-600 text-white text-xs rounded transition-colors"
                              >
                                Copy cURL
                              </button>
                            </div>
                          </details>
                        )}

                        {entry.response && (
                          <details className="mt-2">
                            <summary className="text-xs text-gray-500 dark:text-gray-400 cursor-pointer hover:text-gray-700 dark:hover:text-gray-300">
                              View Response
                            </summary>
                            <div className="mt-2 bg-white dark:bg-gray-900 rounded p-2 font-mono max-h-32 overflow-y-auto">
                              <pre className="whitespace-pre-wrap text-xs">
                                {JSON.stringify(entry.response, null, 2)}
                              </pre>
                            </div>
                          </details>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="flex items-center justify-center h-32">
                    <div className="text-center">
                      <svg className="w-8 h-8 text-gray-400 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                      </svg>
                      <p className="text-gray-500 dark:text-gray-400">No selection history yet</p>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </motion.div>

      {/* Overlay */}
      {showApiPanel && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          onClick={() => setShowApiPanel(false)}
          className="fixed inset-0 bg-black bg-opacity-25 z-40"
        />
      )}

      {/* Floating Theme Toggle Button */}
      <motion.button
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={toggleTheme}
        className="fixed bottom-6 right-6 z-50 w-14 h-14 bg-[#FE4215] hover:bg-[#E53910] text-white rounded-full shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center"
        title={isDarkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
      >
        <motion.div
          initial={false}
          animate={{ rotate: isDarkMode ? 180 : 0 }}
          transition={{ duration: 0.3 }}
        >
          {isDarkMode ? (
            <Sun className="w-6 h-6" />
          ) : (
            <Moon className="w-6 h-6" />
          )}
        </motion.div>
      </motion.button>

      {/* Toast Notification */}
      {/* Toast Notifications */}
      <div className="fixed bottom-20 right-6 z-50 space-y-3 pointer-events-none">
        {toasts.map((toast, index) => (
          <motion.div
            key={toast.id}
            initial={{ opacity: 0, y: 50, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 50, scale: 0.9 }}
            transition={{ duration: 0.3 }}
            style={{ zIndex: 1000 + index }}
            className={`pointer-events-auto rounded-xl shadow-2xl border-l-4 max-w-md ${
              toast.type === 'success'
                ? 'bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border-green-500 dark:border-green-400'
                : 'bg-gradient-to-r from-red-50 to-rose-50 dark:from-red-900/20 dark:to-rose-900/20 border-red-500 dark:border-red-400'
            }`}
          >
            <div className="p-4">
              <div className="flex items-start space-x-3">
                <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
                  toast.type === 'success' 
                    ? 'bg-green-500 dark:bg-green-400' 
                    : 'bg-red-500 dark:bg-red-400'
                }`}>
                  {toast.type === 'success' ? (
                    <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  )}
                </div>
                
                <div className="flex-1">
                  <div className={`text-sm font-medium ${
                    toast.type === 'success' 
                      ? 'text-green-800 dark:text-green-100' 
                      : 'text-red-800 dark:text-red-100'
                  }`}>
                    {toast.message}
                  </div>
                  {toast.dismissible && (
                    <div className={`text-xs mt-1 ${
                      toast.type === 'success' 
                        ? 'text-green-600 dark:text-green-300' 
                        : 'text-red-600 dark:text-red-300'
                    }`}>
                       
                    </div>
                  )}
                </div>
                
                {toast.dismissible && (
                  <button
                    onClick={() => dismissToast(toast.id)}
                    className={`flex-shrink-0 ml-2 p-1 rounded-full hover:bg-black hover:bg-opacity-10 transition-colors ${
                      toast.type === 'success' 
                        ? 'text-green-600 dark:text-green-400' 
                        : 'text-red-600 dark:text-red-400'
                    }`}
                  >
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </button>
                )}
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Demo Tutorial */}
      <DemoTutorial 
        isVisible={showTutorial}
        onClose={handleTutorialClose}
      />
    </div>
  )
}

export default USPFlowerStorePage
